import type { ISubmitSaleInquiryCmd } from "@/data/commands/support/SubmitSaleInquiryCmd";
import { Factory } from "@/data/dao/Factory";
import { SupportDao } from "@/data/dao/service/SupportDao";
import { PricingModel } from "@/data/model/Enums";
import type { IEnrolmentCheckDto } from "@/data/model/Support";
import { Validation } from "@/data/util/Validation";

import { clientLanguage } from "@/shared/localization/language";
import { logError } from "@/shared/logging/errors";
import { FloatLabel } from "@/shared/ui/FloatLabel";
import { Ripple } from "@/shared/ui/Ripple";

import { i18n } from "@/portal/Trans";
import { PasswordInput } from "@/portal/formControls/PasswordInput";
import { PhoneNumberInput } from "@/portal/formControls/PhoneNumberInput";

export class SignupController {
    private supportDao = Factory.getInstance<SupportDao>(SupportDao);

    private $element: JQuery;
    private $form: JQuery;
    private $emailField: JQuery;
    private $submitBtn: JQuery;

    private checkMailDelay?: number;
    private checkMailPromise?: Promise<IEnrolmentCheckDto>;

    private enrolmentState?: IEnrolmentCheckDto;
    private phoneNumberInput: PhoneNumberInput;
    private passwordInput: PasswordInput;

    constructor(element: JQuery) {
        this.$element = element;

        this.$form = this.$element.find(".form");
        this.$form.submit((e) => {
            e.preventDefault();
            e.stopPropagation();

            this.onFormSubmit();

            return false;
        });

        this.phoneNumberInput = new PhoneNumberInput(i18n("general.fields.phone"), true).appendTo(
            this.$form.find("#phonerow")
        );
        this.passwordInput = new PasswordInput(true, true, i18n("signup.fields.password")).appendTo(
            this.$form.find("#passwordrow")
        );

        this.$emailField = this.$form.find("#emailfield");
        this.$emailField.keyup(() => {
            this.checkEnrolmentState(this.$emailField.val());
        });

        this.$emailField.blur(() => {
            this.checkEnrolmentState(this.$emailField.val());
        });

        this.$emailField.change(() => {
            this.checkEnrolmentState(this.$emailField.val());
        });

        if (this.$emailField.val().length !== 0) {
            this.checkEnrolmentState(this.$emailField.val());
        }

        this.$submitBtn = this.$form.find(".submit");

        Ripple.bind(this.$submitBtn);
        FloatLabel.bind(this.$form);
    }

    private checkEnrolmentState(email: string) {
        const emailBox = this.$emailField.parent();
        emailBox.find("> .loader").remove();

        if (this.checkMailDelay !== undefined) {
            clearTimeout(this.checkMailDelay);
            this.checkMailDelay = undefined;
        }

        if (!Validation.isEmail(email)) return;

        this.checkMailDelay = window.setTimeout(async () => {
            const loader = $('<div class="loader"></div>').appendTo(emailBox);

            let currentCheckMailPromise: Promise<IEnrolmentCheckDto> | undefined = undefined;
            try {
                currentCheckMailPromise = this.supportDao.checkEnrolment({
                    email: email,
                    pricingModel: PricingModel.Free,
                });
                this.checkMailPromise = currentCheckMailPromise;
                const enrollmentState = await currentCheckMailPromise;

                if (this.checkMailPromise != currentCheckMailPromise) {
                    return;
                }

                this.$submitBtn.removeClass("disabled").prop("disabled", false);

                loader.remove();
                this.enrolmentState = enrollmentState;
                this.updateFormByAccountState();
            } catch (ex) {
                if (this.checkMailPromise != currentCheckMailPromise) return;

                logError(ex);
                this.$submitBtn.addClass("disabled").prop("disabled", true);

                loader.remove();
                this.enrolmentState = undefined;
                this.updateFormByAccountState();
            }
        }, 400);
    }

    private updateFormByAccountState() {
        let showName = true;
        let showPw = true;
        let showPhone = true;
        let showMessage = false;

        const messageContainer = this.$form.find("#message").stop(true, true);

        if (this.enrolmentState !== undefined) {
            if (this.enrolmentState.accountExists) {
                if (this.enrolmentState.enrolmentComplete) {
                    messageContainer.html(i18n("signup.success"));
                    showMessage = true;
                    showName = false;
                    showPw = false;
                    showPhone = false;
                    this.$submitBtn.addClass("disabled").prop("disabled", true);
                } else {
                    showPw = false;
                    showName = false;
                    showPhone = this.enrolmentState.missingFields.includes("phone");
                }
            }
        } else {
            showName = false;
            showPw = false;
            showPhone = false;
            showMessage = false;
        }

        const nameRow = this.$form.find("#namerow").stop(true, true);
        if (showName) {
            if (!nameRow.is(":visible")) nameRow.slideDown(400, "easeOutExpo");
        } else {
            if (nameRow.is(":visible")) nameRow.slideUp(400, "easeOutExpo");
        }

        if (showMessage) {
            if (!messageContainer.is(":visible")) messageContainer.slideDown(400, "easeOutExpo");
        } else {
            if (messageContainer.is(":visible")) messageContainer.slideUp(400, "easeOutExpo");
        }

        const phoneRow = this.$form.find("#phonerow").stop(true, true);
        if (showPhone) {
            if (!phoneRow.is(":visible")) phoneRow.slideDown(400, "easeOutExpo");
        } else {
            if (phoneRow.is(":visible")) phoneRow.slideUp(400, "easeOutExpo");
        }

        const pwRow = this.$form.find("#passwordrow").stop(true, true);
        if (showPw) {
            if (!pwRow.is(":visible")) pwRow.slideDown(400, "easeOutExpo");
        } else {
            if (pwRow.is(":visible")) pwRow.slideUp(400, "easeOutExpo");
        }
    }

    private async onFormSubmit() {
        if (this.$submitBtn.hasClass("loading")) return;

        const companyField = this.$form.find("#companyfield");
        const locationField = this.$form.find("#locationfield");
        const websiteField = this.$form.find("#websitefield");
        const emailField = this.$form.find("#emailfield");

        const cmd: ISubmitSaleInquiryCmd = {
            company: companyField.val().length === 0 ? undefined : companyField.val(),
            email: emailField.val().length === 0 ? undefined : emailField.val(),
            location: locationField.val().length === 0 ? undefined : locationField.val(),
            website: websiteField.val().length === 0 ? undefined : websiteField.val(),
            pricingModel: PricingModel.Free,
            language: clientLanguage(),
        };

        let hasError = false;
        this.$form.find(".has-error").removeClass("has-error");

        if (cmd.company === undefined) {
            hasError = true;
            companyField.parent().addClass("has-error");
        }

        if (cmd.location === undefined) {
            hasError = true;
            locationField.parent().addClass("has-error");
        }

        if (cmd.website !== undefined && !Validation.isUrl(cmd.website)) {
            hasError = true;
            websiteField.parent().addClass("has-error");
        }

        if (this.enrolmentState === undefined) {
            hasError = true;
            emailField.parent().addClass("has-error");
        } else {
            if (!this.enrolmentState.accountExists) {
                const firstNameField = this.$form.find("#firstnamefield");
                cmd.firstName = firstNameField.val().length === 0 ? undefined : firstNameField.val();
                if (cmd.firstName === undefined) {
                    hasError = true;
                    firstNameField.parent().addClass("has-error");
                }

                const lastNameField = this.$form.find("#lastnamefield");
                cmd.lastName = lastNameField.val().length === 0 ? undefined : lastNameField.val();
                if (cmd.lastName === undefined) {
                    hasError = true;
                    lastNameField.parent().addClass("has-error");
                }

                if (!Validation.isEmail(cmd.email)) {
                    hasError = true;
                    emailField.parent().addClass("has-error");
                }

                cmd.password = this.passwordInput.getValue();
                if (cmd.password === undefined || !this.passwordInput.isValid()) {
                    hasError = true;
                    this.passwordInput.setError(true);
                } else {
                    this.passwordInput.setError(false);
                }
            }

            if (!this.enrolmentState.accountExists || this.enrolmentState.missingFields.includes("phone")) {
                cmd.phoneNumber = this.phoneNumberInput.getNumber();
                cmd.phoneCountryCode = this.phoneNumberInput.getCountryCode();

                if (
                    cmd.phoneNumber === undefined ||
                    cmd.phoneCountryCode === undefined ||
                    !this.phoneNumberInput.isValid()
                ) {
                    hasError = true;
                    this.phoneNumberInput.setError(true);
                } else this.phoneNumberInput.setError(false);
            }
        }

        if (hasError) return;

        this.$submitBtn.addClass("loading").prop("disabled", true);

        try {
            await this.supportDao.submitInquiry(cmd);
            this.showSuccess(cmd.email);
        } catch (ex) {
            logError(ex);
            alert(i18n("signup.failed"));
            this.$submitBtn.removeClass("loading").prop("disabled", false);
        }
    }

    private showSuccess(email: string) {
        this.$form.parent().slideUp(400, "easeOutExpo", () => {
            const nP = this.$form.parent().empty();
            const txt = i18n("signup.accessCreated", { email: email });

            const sc = $('<div class="success"></div>').appendTo(nP);
            sc.append('<div class="check"><i class="fas fa-check"></i></div>');
            sc.append('<div class="msg">' + txt + "</div>");

            nP.fadeIn(400, "easeOutExpo", () => {
                sc.addClass("shown");
            });
        });
    }
}
